/* assets/safety-moments.js
 *
 * Safety Moments client-side behavior:
 *   - Filter chips (single-select Format, multi-select Industry + Category)
 *   - Random pick (respects active filters; picks from visible cards)
 *   - localStorage bookmarks (anonymous + approved customers alike)
 *   - All / Saved view toggle
 *   - Print on the detail page
 *
 * No dependencies. Loads on both the library section and the detail section
 * — each block early-returns when its target DOM isn't present.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'dropsafe:saved-safety-moments';
  const CHECKS_KEY = 'dropsafe:safety-moment-checks';

  // ---------------------------------------------------------------------------
  // Bookmark store
  // ---------------------------------------------------------------------------
  const store = {
    read() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return new Set();
        const arr = JSON.parse(raw);
        return new Set(Array.isArray(arr) ? arr : []);
      } catch (e) {
        return new Set();
      }
    },
    write(set) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
      } catch (e) {
        /* quota or private mode — silently ignore */
      }
    },
    toggle(handle) {
      const set = this.read();
      if (set.has(handle)) set.delete(handle);
      else set.add(handle);
      this.write(set);
      return set;
    },
  };

  // Cross-tab + same-page subscribers.
  const bookmarkListeners = new Set();
  function emitBookmarkChange() {
    const set = store.read();
    bookmarkListeners.forEach((fn) => fn(set));
  }
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) emitBookmarkChange();
  });

  // ---------------------------------------------------------------------------
  // Localized count templates
  // ---------------------------------------------------------------------------
  // The element carries data-template-one / data-template-other with literal
  // %count% placeholders (rendered server-side via locales/*.json — we use
  // %count% rather than {count} because the latter trips Liquid's parser
  // when used as a string argument to the `replace` filter). We pick the
  // plural form by count and substitute. Avoids regex over rendered text
  // and respects translator-provided plural rules.
  function applyCountTemplate(el, count) {
    if (!el) return;
    const tplOther = el.dataset.templateOther;
    if (!tplOther) return;
    const tplOne = el.dataset.templateOne;
    const tpl = count === 1 && tplOne ? tplOne : tplOther;
    el.textContent = tpl.replace('%count%', String(count));
  }

  // ---------------------------------------------------------------------------
  // Bookmark button — shared sync for card + detail variants
  // ---------------------------------------------------------------------------
  function syncBookmarkButton(btn, isSaved) {
    btn.classList.toggle('is-saved', isSaved);
    btn.setAttribute('aria-pressed', isSaved ? 'true' : 'false');

    // Visible label (detail variant has a child span; card variant has none).
    const visibleLabel = btn.querySelector('[data-sm-detail-bookmark-label]');
    if (visibleLabel) {
      const save = btn.dataset.labelSave;
      const saved = btn.dataset.labelSaved;
      if (save && saved) visibleLabel.textContent = isSaved ? saved : save;
    }

    // ARIA label (card variant has no visible text; toggle for SR users).
    const saveAria = btn.dataset.labelSaveAria;
    const savedAria = btn.dataset.labelSavedAria;
    if (saveAria && savedAria) {
      btn.setAttribute('aria-label', isSaved ? savedAria : saveAria);
    }
  }

  // ---------------------------------------------------------------------------
  // Library page
  // ---------------------------------------------------------------------------
  function initLibrary() {
    const root = document.querySelector('[data-sm-library]');
    if (!root) return;

    const grid = root.querySelector('[data-sm-grid]');
    const cards = [...root.querySelectorAll('[data-sm-card]')];
    if (!cards.length) return;

    const filters = root.querySelector('[data-sm-filters]');
    const countEl = root.querySelector('[data-sm-count]');
    const clearBtn = root.querySelector('[data-sm-clear]');
    const emptyAll = root.querySelector('[data-sm-empty]');
    const emptySaved = root.querySelector('[data-sm-empty-saved]');
    const tabs = [...root.querySelectorAll('[data-sm-tab]')];
    const savedTabLabel = root.querySelector('[data-sm-tab-saved-label]');

    const state = {
      format: '',
      industries: new Set(),
      categories: new Set(),
      tab: 'all',
    };

    // Filter state ------------------------------------------------------------
    // Multi-select state (industries / categories) lives in the Sets above.
    // The DOM has two surfaces representing that state: dropdown __option
    // buttons (in popovers) and the removable __chip pills in the selected
    // row. syncMulti() pushes state out to both so they stay in lockstep,
    // and the click handler below mutates state then calls syncMulti+render.
    const selectedRow = root.querySelector('[data-sm-selected]');
    const dropdownWraps = [...root.querySelectorAll('.sm-dropdown-wrap')];

    // Build a map: handle -> label, by reading the option button text.
    // Used when rendering removable chips so we don't re-fetch from Liquid.
    const labelMap = { industries: {}, categories: {} };
    dropdownWraps.forEach((wrap) => {
      const key = wrap.dataset.smFilterGroup;
      if (!key || !labelMap[key]) return;
      wrap.querySelectorAll('[data-sm-chip]').forEach((opt) => {
        const handle = opt.dataset.smChip;
        const labelEl = opt.querySelector('span:last-child');
        labelMap[key][handle] = labelEl ? labelEl.textContent.trim() : handle;
      });
    });

    function syncMulti() {
      dropdownWraps.forEach((wrap) => {
        const key = wrap.dataset.smFilterGroup;
        const set = state[key];
        if (!set) return;
        wrap.querySelectorAll('[data-sm-chip]').forEach((opt) => {
          const on = set.has(opt.dataset.smChip);
          opt.classList.toggle('is-active', on);
          opt.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        const countEl = wrap.querySelector('[data-sm-dropdown-count]');
        if (countEl) {
          if (set.size > 0) {
            countEl.hidden = false;
            countEl.textContent = String(set.size);
          } else {
            countEl.hidden = true;
          }
        }
      });
      renderSelectedRow();
    }

    function renderSelectedRow() {
      if (!selectedRow) return;
      const items = [];
      ['industries', 'categories'].forEach((key) => {
        state[key].forEach((handle) => {
          items.push({ key, handle, label: labelMap[key][handle] || handle });
        });
      });
      if (!items.length) {
        selectedRow.hidden = true;
        selectedRow.textContent = '';
        return;
      }
      selectedRow.hidden = false;
      selectedRow.textContent = '';
      items.forEach(({ key, handle, label }) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sm-selected__chip';
        btn.dataset.smSelectedRemove = '';
        btn.dataset.key = key;
        btn.dataset.handle = handle;
        const ariaTpl = selectedRow.dataset.ariaTemplate || 'Remove %label%';
        btn.setAttribute('aria-label', ariaTpl.replace('%label%', label));
        const text = document.createElement('span');
        text.textContent = label;
        const x = document.createElement('span');
        x.className = 'sm-selected__chip-x';
        x.setAttribute('aria-hidden', 'true');
        x.textContent = '×';
        btn.append(text, x);
        selectedRow.append(btn);
      });
    }

    if (selectedRow) {
      selectedRow.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-sm-selected-remove]');
        if (!btn) return;
        const { key, handle } = btn.dataset;
        if (state[key] && state[key].has(handle)) {
          state[key].delete(handle);
          syncMulti();
          render();
        }
      });
    }

    // Chip handling -----------------------------------------------------------
    if (filters) {
      filters.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-sm-chip]');
        if (!chip) return;
        // The dropdown trigger button is not a chip — bail out cleanly.
        if (chip.matches('[data-sm-dropdown-trigger]')) return;
        const group = chip.closest('[data-sm-filter-group], .sm-dropdown-wrap');
        if (!group) return;
        const key = group.dataset.smFilterGroup;
        const mode = group.dataset.smMode;
        const value = chip.dataset.smChip;

        if (mode === 'single') {
          state[key] = value;
          group.querySelectorAll('[data-sm-chip]').forEach((c) => {
            const active = c.dataset.smChip === value;
            c.classList.toggle('is-active', active);
            c.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
        } else {
          const set = state[key];
          if (set.has(value)) set.delete(value);
          else set.add(value);
          syncMulti();
        }
        render();
      });
    }

    // Dropdown open/close -----------------------------------------------------
    function closeAllDropdowns(except) {
      dropdownWraps.forEach((wrap) => {
        if (wrap === except) return;
        const trigger = wrap.querySelector('[data-sm-dropdown-trigger]');
        const panel = wrap.querySelector('[data-sm-dropdown-panel]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (panel) panel.hidden = true;
      });
    }

    dropdownWraps.forEach((wrap) => {
      const trigger = wrap.querySelector('[data-sm-dropdown-trigger]');
      const panel = wrap.querySelector('[data-sm-dropdown-panel]');
      if (!trigger || !panel) return;
      trigger.addEventListener('click', () => {
        const open = trigger.getAttribute('aria-expanded') === 'true';
        if (open) {
          trigger.setAttribute('aria-expanded', 'false');
          panel.hidden = true;
        } else {
          closeAllDropdowns(wrap);
          trigger.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
        }
      });
    });

    if (dropdownWraps.length) {
      document.addEventListener('click', (e) => {
        if (e.target.closest('.sm-dropdown-wrap')) return;
        closeAllDropdowns(null);
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeAllDropdowns(null);
      });
    }

    // View toggle (All / Saved) ----------------------------------------------
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const value = tab.dataset.smTab;
        if (state.tab === value) return;
        state.tab = value;
        tabs.forEach((t) => {
          const active = t.dataset.smTab === value;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        render();
      });
    });

    // Clear filters -----------------------------------------------------------
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        state.format = '';
        state.industries.clear();
        state.categories.clear();
        // Single-select (Format) chips
        root.querySelectorAll('[data-sm-filter-group] [data-sm-chip]').forEach((c) => {
          const group = c.closest('[data-sm-filter-group]');
          const mode = group && group.dataset.smMode;
          const isAllChip = mode === 'single' && c.dataset.smChip === '';
          c.classList.toggle('is-active', isAllChip);
          c.setAttribute('aria-pressed', isAllChip ? 'true' : 'false');
        });
        // Multi-select (Industry / Topic) — dropdown options + chip row
        syncMulti();
        render();
      });
    }

    // Random pick -------------------------------------------------------------
    const randomBtn = root.querySelector('[data-sm-random]');
    const randomLabel = root.querySelector('[data-sm-random-label]');
    const randomPanel = root.querySelector('[data-sm-random-panel]');
    const randomTitle = root.querySelector('[data-sm-random-title]');
    const randomTakeaway = root.querySelector('[data-sm-random-takeaway]');
    const randomDuration = root.querySelector('[data-sm-random-duration]');
    const randomFormat = root.querySelector('[data-sm-random-format]');
    const randomLink = root.querySelector('[data-sm-random-link]');
    if (randomBtn) {
      const againText = randomBtn.dataset.againText || '';

      randomBtn.addEventListener('click', () => {
        const pool = cards.filter((c) => matchesFilters(c) && !c.hidden);
        if (!pool.length) return;
        const pick = pool[Math.floor(Math.random() * pool.length)];
        const fmt = pick.querySelector('.sm-card__format');
        const dur = pick.querySelector('.sm-card__duration');
        const link = pick.querySelector('.sm-card__link');

        if (randomTitle) randomTitle.textContent = pick.dataset.title || '';
        if (randomTakeaway) randomTakeaway.textContent = pick.dataset.takeaway || '';
        if (randomDuration) randomDuration.textContent = dur ? dur.textContent.trim() : '';
        if (randomFormat) randomFormat.textContent = fmt ? fmt.textContent.trim() : '';
        if (randomLink && link) randomLink.setAttribute('href', link.getAttribute('href'));
        if (randomPanel) randomPanel.hidden = false;
        if (randomLabel && againText) randomLabel.textContent = againText;
      });
    }

    // Render ------------------------------------------------------------------
    function matchesFilters(card) {
      if (state.format && card.dataset.format !== state.format) return false;

      if (state.industries.size) {
        const cardInds = (card.dataset.industries || '').split(',').filter(Boolean);
        if (!cardInds.some((i) => state.industries.has(i))) return false;
      }

      if (state.categories.size) {
        const cardCats = (card.dataset.categories || '').split(',').filter(Boolean);
        if (!cardCats.some((c) => state.categories.has(c))) return false;
      }
      return true;
    }

    function matchesTab(card, saved) {
      if (state.tab !== 'saved') return true;
      return saved.has(card.dataset.handle);
    }

    function anyFiltersActive() {
      return !!(state.format || state.industries.size || state.categories.size);
    }

    function render() {
      const saved = store.read();
      let visible = 0;
      cards.forEach((card) => {
        const show = matchesFilters(card) && matchesTab(card, saved);
        card.hidden = !show;
        if (show) visible++;
        const btn = card.querySelector('[data-sm-bookmark]');
        if (btn) syncBookmarkButton(btn, saved.has(card.dataset.handle));
      });

      applyCountTemplate(countEl, visible);
      if (clearBtn) clearBtn.hidden = !anyFiltersActive();

      if (state.tab === 'saved') {
        if (emptySaved) emptySaved.hidden = visible > 0;
        if (emptyAll) emptyAll.hidden = true;
      } else {
        if (emptyAll) emptyAll.hidden = visible > 0;
        if (emptySaved) emptySaved.hidden = true;
      }

      applyCountTemplate(savedTabLabel, saved.size);
    }

    // Bookmark buttons on cards -----------------------------------------------
    // Bookmark <button> is a sibling of the <a> link (not nested), so a normal
    // click handler is enough — no preventDefault/stopPropagation gymnastics.
    if (grid) {
      grid.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-sm-bookmark]');
        if (!btn) return;
        const handle = btn.dataset.smBookmark;
        store.toggle(handle);
        emitBookmarkChange();
      });
    }

    bookmarkListeners.add(render);
    render();
  }

  // ---------------------------------------------------------------------------
  // Detail page
  // ---------------------------------------------------------------------------
  function initDetail() {
    const root = document.querySelector('[data-sm-detail]');
    if (!root) return;

    const handle = root.dataset.handle;
    const bookmarkBtn = root.querySelector('[data-sm-detail-bookmark]');
    const printBtn = root.querySelector('[data-sm-print]');

    if (bookmarkBtn && handle) {
      const update = () => syncBookmarkButton(bookmarkBtn, store.read().has(handle));
      bookmarkBtn.addEventListener('click', () => {
        store.toggle(handle);
        emitBookmarkChange();
      });
      bookmarkListeners.add(update);
      update();
    }

    if (printBtn) {
      printBtn.addEventListener('click', () => window.print());
    }

    initChecklist(root);
  }

  // ---------------------------------------------------------------------------
  // Detail-page checklist — discussion questions + action item.
  // State persists in localStorage scoped per-moment handle so a foreman who
  // reopens the page (or comes back tomorrow) sees the same ticks. The Reset
  // button only shows when at least one item is checked.
  // ---------------------------------------------------------------------------
  function readChecks() {
    try {
      const raw = localStorage.getItem(CHECKS_KEY);
      if (!raw) return {};
      const obj = JSON.parse(raw);
      return obj && typeof obj === 'object' ? obj : {};
    } catch (e) {
      return {};
    }
  }

  function writeChecks(all) {
    try {
      localStorage.setItem(CHECKS_KEY, JSON.stringify(all));
    } catch (e) {
      /* quota / private mode — silently ignore */
    }
  }

  function initChecklist(detailRoot) {
    const checklist = detailRoot.querySelector('[data-sm-checklist]');
    if (!checklist) return;

    const handle = checklist.dataset.handle;
    if (!handle) return;

    const inputs = [...checklist.querySelectorAll('[data-sm-check]')];
    if (!inputs.length) return;

    const resetBtn = checklist.querySelector('[data-sm-checklist-reset]');

    function loadForHandle() {
      const all = readChecks();
      const mine = all[handle];
      return mine && typeof mine === 'object' ? mine : {};
    }

    function saveForHandle(mine) {
      const all = readChecks();
      if (Object.keys(mine).length === 0) {
        delete all[handle];
      } else {
        all[handle] = mine;
      }
      writeChecks(all);
    }

    function syncResetVisibility() {
      if (!resetBtn) return;
      const anyChecked = inputs.some((i) => i.checked);
      resetBtn.hidden = !anyChecked;
    }

    function applyToItem(input) {
      const item = input.closest('[data-sm-check-item]');
      if (item) item.classList.toggle('is-checked', input.checked);
    }

    // Hydrate from storage on load.
    const stored = loadForHandle();
    inputs.forEach((input) => {
      const key = input.dataset.smCheck;
      if (stored[key]) input.checked = true;
      applyToItem(input);
      input.addEventListener('change', () => {
        const mine = loadForHandle();
        if (input.checked) {
          mine[input.dataset.smCheck] = 1;
        } else {
          delete mine[input.dataset.smCheck];
        }
        saveForHandle(mine);
        applyToItem(input);
        syncResetVisibility();
      });
    });

    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        inputs.forEach((input) => {
          input.checked = false;
          applyToItem(input);
        });
        saveForHandle({});
        syncResetVisibility();
      });
    }

    syncResetVisibility();
  }

  function init() {
    initLibrary();
    initDetail();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
